import { operations as DmsmOperations } from '../types/apis/dmsm/schema-typescript';
import type { CiProjectSpendShiftApiResponse } from '../components/SupplierSpendShift.types';
import { operations as SamOperations } from '../types/apis/sam/schema-typescript';
import { operations as SupplierLocationOperations } from '../types/apis/supplierLocation/schema-typescript';
import { operations as SroOperations } from '../types/apis/sro/schema-typescript';
import { operations as SepOperations } from '../types/apis/sep/schema-typescript';

import {
  MGC_DIRECT,
  NOT_AVAILABLE,
  SAM_APP_CODE,
  SCOPE_LEVEL_GLOBAL_AFTERMARKET,
  SCOPE_LEVEL_GLOBAL_PRODUCTION,
  STRATEGY_STATUS_DELETED
} from './AppConstants';
import { addPropAndValue } from './UtilFunctions';
import {
  aeApi,
  dmsmApi,
  riskRatingApi,
  samApi,
  sdiCertApi,
  sepApi,
  sroApi,
  supplierLocationApi
} from '../utils/api-clients/api-clients';

const defaultUser = {
  firstName: NOT_AVAILABLE,
  middleInitial: NOT_AVAILABLE,
  lastName: NOT_AVAILABLE,
  email: NOT_AVAILABLE,
  isEmployee: false,
  upperFirstName: NOT_AVAILABLE,
  upperLastName: NOT_AVAILABLE
};

function getDefaultUser(userId: any) {
  return { ...defaultUser, id: userId };
}
function processUserInfoAPIResponse(
  success: boolean,
  resultList: any[],
  addUpperCaseNames: boolean,
  setterMapFn?: any,
  setterListFn?: any
) {
  let result = resultList;

  if (!success) {
    result = resultList.map((userId) => getDefaultUser(userId));
  }

  if (addUpperCaseNames && success) {
    result = result.map((user) => {
      addPropAndValue(user, 'upperFirstName', user.firstName.toUpperCase());
      addPropAndValue(user, 'upperLastName', user.lastName.toUpperCase());
      return user;
    });
  }
  if (setterMapFn) {
    let map = new Map();

    result.forEach((user) => map.set(user.id.toUpperCase(), user));
    setterMapFn(map);
  }
  setterListFn?.(result);
}
export async function callUserInfoAPI(
  userIds: DmsmOperations['getUserInfoForUserIds']['requestBody']['content']['application/json'],
  setterMapFn: any,
  setterListFn: any,
  incrementAPIInProgress: any,
  decrementAPIInProgress: any,
  setStatusErrorMsg: any,
  addUpperCaseNames: boolean
) {
  if (userIds && userIds.length > 0) {
    incrementAPIInProgress();

    const { data, error } = await dmsmApi.fetchClient.request(
      ...dmsmApi.operations['getUserInfoForUserIds'],
      {
        body: userIds
      }
    );

    if (error) {
      setStatusErrorMsg(
        "Couldn't retrieve User Info. Please try again.",
        error
      );
      // Process response with just userIds
      processUserInfoAPIResponse(
        false,
        userIds,
        addUpperCaseNames,
        setterMapFn,
        setterListFn
      );
    } else {
      processUserInfoAPIResponse(
        true,
        data,
        addUpperCaseNames,
        setterMapFn,
        setterListFn
      );
    }

    decrementAPIInProgress();
  }
}

export async function callMGCMasterAPI(
  setterFn: any,
  incrementAPIInProgress: any,
  decrementAPIInProgress: any,
  setStatusErrorMsg: any,
  direct: boolean
) {
  incrementAPIInProgress();

  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['findMasterMGCs']
  );

  if (error) {
    setStatusErrorMsg(
      "Couldn't load the Master MGC Info. Please try again.",
      error
    );
  } else {
    if (direct) {
      setterFn(
        data.filter((record) => {
          return record.code.includes(MGC_DIRECT);
        })
      );
    } else {
      setterFn(data);
    }
  }
  decrementAPIInProgress();
}

export async function callMasterScopeAPI(
  incrementAPIInProgress: any,
  decrementAPIInProgress: any,
  setStatusErrorMsg: any,
  setScopeInfo: any,
  setScopeUSIndicatorMap: any
) {
  incrementAPIInProgress();
  let usIndMap = new Map();

  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['getScope']
  );

  if (error) {
    setStatusErrorMsg("Couldn't load master Scope. Please try again.", error);
  } else {
    if (setScopeUSIndicatorMap) {
      // Create Map of unitGroupCode/unitCode to usIndicator for quick lookup
      const groupsMap = new Map(Object.entries(data.unitGroupsMap));
      const unitsMap = new Map(Object.entries(data.unitsMap));
      let hasGlobalProductionUSIndicator = false;
      let hasGlobalAftermarketUSIndicator = false;

      groupsMap.forEach((unitGroup, key) => {
        if (unitGroup.usIndicator) {
          if (unitGroup.globalLevelName === SCOPE_LEVEL_GLOBAL_PRODUCTION) {
            hasGlobalProductionUSIndicator = true;
          } else if (
            unitGroup.globalLevelName === SCOPE_LEVEL_GLOBAL_AFTERMARKET
          ) {
            hasGlobalAftermarketUSIndicator = true;
          }
          usIndMap.set(unitGroup.code, unitGroup.usIndicator);
        }
      });
      unitsMap.forEach((unit, key) => {
        if (unit.usIndicator) {
          usIndMap.set(unit.code, unit.usIndicator);
        }
      });
      usIndMap.set(
        SCOPE_LEVEL_GLOBAL_PRODUCTION,
        hasGlobalProductionUSIndicator
      );
      usIndMap.set(
        SCOPE_LEVEL_GLOBAL_AFTERMARKET,
        hasGlobalAftermarketUSIndicator
      );
      setScopeUSIndicatorMap(usIndMap);
      setScopeInfo(data);
    }
  }
  decrementAPIInProgress();
}

/*
  Find users in SRO role
*/
export async function callUsersInSRO_RoleAPI(
  sroRole: DmsmOperations['getUsersInSRORole']['parameters']['path']['roleId'],
  setterMapFn: any,
  setterListFn: any,
  incrementAPIInProgress: any,
  decrementAPIInProgress: any,
  setStatusErrorMsg: any
) {
  incrementAPIInProgress();

  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['getUsersInSRORole'],
    {
      params: {
        path: {
          roleId: sroRole
        }
      }
    }
  );

  if (error) {
    setStatusErrorMsg(
      "Couldn't retrieve Users in SRO Role Info. Please try again.",
      error
    );
  } else {
    if (setterMapFn) {
      let map = new Map();

      data.forEach((userId) => {
        map.set(userId, userId);
      });
      setterMapFn(map);
    }
    setterListFn?.(data);
  }
  decrementAPIInProgress();
}

/*
  Find users in SAM role
*/
export async function callUsersInSAM_RoleAPI(
  samRole: DmsmOperations['getSAMUsersInRole']['parameters']['path']['roleId'],
  setterMapFn: any,
  setterListFn: any,
  incrementAPIInProgress: any,
  decrementAPIInProgress: any,
  setStatusErrorMsg: any
) {
  incrementAPIInProgress();

  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['getSAMUsersInRole'],
    {
      params: {
        path: {
          appId: SAM_APP_CODE,
          roleId: samRole
        }
      }
    }
  );

  if (error) {
    setStatusErrorMsg(
      "Couldn't retrieve SAM Role User Info. Please try again.",
      error
    );
  } else {
    if (setterMapFn) {
      let map = new Map();

      data.forEach((userId) => {
        map.set(userId, userId);
      });
      setterMapFn(map);
    }
    setterListFn?.(data);
  }
  decrementAPIInProgress();
}

function getAsMap(object: any) {
  let jsonMap = JSON.stringify(object);
  return new Map(Object.entries(JSON.parse(jsonMap)));
}

// New
export async function callStrategiesGetAPI(
  strategyNumber: DmsmOperations['findStrategyById']['parameters']['path']['strategyId'],
  incrementAPIInProgress: any,
  decrementAPIInProgress: any,
  setStatusErrorMsg: any,
  setStrategyInformation: any,
  setSelectedSuppliers: any,
  setStrategySupplierUsers: any
) {
  incrementAPIInProgress();

  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['findStrategyById'],
    {
      params: {
        path: {
          strategyId: strategyNumber
        },
        query: {
          embed: 'forecast_spend,invoice_spend'
        }
      }
    }
  );

  if (error) {
    setStatusErrorMsg("Couldn't load the strategy. Please try again.", error);
  } else {
    if ('404' in (data as Record<string, unknown>)) {
      setStatusErrorMsg(
        (data as any)['404'] || 'StrategyId does not exist: ' + strategyNumber
      );
    } else if (data.status === STRATEGY_STATUS_DELETED) {
      setStatusErrorMsg('StrategyId does not exist: ' + strategyNumber);
    } else {
      setStrategyInformation(data);

      if (data?.suppliers) {
        // eslint-disable-next-line array-callback-return
        data.suppliers.map((supplier) => {
          if (supplier?.scopeOrganizedStatusExceptions) {
            let supplierStatusExceptionsMap = getAsMap(
              supplier.scopeOrganizedStatusExceptions
            );

            for (let [, value] of supplierStatusExceptionsMap.entries()) {
              const v = value as any;
              if (v?.unitStatusMap) {
                let newUnitMap = getAsMap(v.unitStatusMap);
                v.statusMap = newUnitMap;
              }
            }
            supplier.scopeOrganizedStatusExceptions =
              supplierStatusExceptionsMap as any;
            supplier.statusMap = supplierStatusExceptionsMap as any;
          }
        });
      }
      setSelectedSuppliers?.(data.suppliers);

      if (setStrategySupplierUsers) {
        const supplierUpdatedId: string[] = [];
        const upcaseOwnerId = data.ownerId?.toUpperCase();

        if (data.suppliers && data.suppliers.length > 0) {
          data.suppliers.forEach((item) => {
            if (
              item.updatedById &&
              item.updatedById.toUpperCase() !== upcaseOwnerId
            )
              supplierUpdatedId.push(item.updatedById);
          });
        }
        if (
          data.nonStrategySupplierList &&
          data.nonStrategySupplierList.length > 0
        ) {
          data.nonStrategySupplierList.forEach((item) => {
            if (
              item.updatedById &&
              item.updatedById.toUpperCase() !== upcaseOwnerId
            )
              supplierUpdatedId.push(item.updatedById);
          });
        }
        setStrategySupplierUsers(supplierUpdatedId);
      }
    }
  }

  decrementAPIInProgress();
}

export async function recordSAMUserActivity(
  userId: SamOperations['ApplicationsController_setAppUsageStats']['parameters']['path']['userId'],
  appId: SamOperations['ApplicationsController_setAppUsageStats']['parameters']['path']['appId']
) {
  const { data, error } = await samApi.fetchClient.request(
    ...samApi.operations['ApplicationsController_setAppUsageStats'],
    {
      body: {
        //body type calls for pageNm being included, but the legacy app didn't have it. At time of typescript conversion, we decided to not pass this param through
        type: '',
        url: '',
        itemHeader: ''
      },
      params: {
        path: {
          appId: appId,
          userId: userId,
          mobileInd: 'N'
        }
      }
    }
  );

  if (error) {
    console.log('SAM Usage Stats API Failure: ' + error);
  }
}

export async function getSupplierLocationInfo(
  supplierNumbers: SupplierLocationOperations['retrieveSupplierList']['requestBody']['content']['application/json']['supplierNumbers']
) {
  if (!Array.isArray(supplierNumbers)) {
    supplierNumbers = [supplierNumbers];
  }

  const { data, error } = await supplierLocationApi.fetchClient.request(
    ...supplierLocationApi.operations['retrieveSupplierList'],
    {
      body: {
        supplierNumbers: supplierNumbers
      }
    }
  );

  if (error) {
    throw new Error("Couldn't load supplier location info. Please try again.");
  }

  const nameMap = new Map();
  const locationMap = new Map();
  const supplierStatusMap = new Map();

  data.forEach((supplier) => {
    nameMap.set(supplier.supplierNumber, supplier.supplierName);
    locationMap.set(
      supplier.supplierNumber,
      supplier.city + ', ' + supplier.state + ' ' + supplier.country
    );
    supplierStatusMap.set(supplier.supplierNumber, supplier.activeSupplierType);
  });

  return { nameMap, locationMap, supplierStatusMap };
}

export async function getSdiMap(
  supplierNumbers: string[] // operation request body is untyped, so using string[] directly
) {
  if (!Array.isArray(supplierNumbers)) {
    supplierNumbers = [supplierNumbers];
  }

  const { data, error } = await sdiCertApi.fetchClient.request(
    ...sdiCertApi.operations['handleSingleSupplierSdi'],
    {
      body: {
        supplierNumbers: supplierNumbers
      }
    }
  );

  if (error) {
    throw new Error("Couldn't load SDI info. Please try again.");
  }
  return new Map(Object.entries(data));
}

// Risk rating spec was incorrect last we checked, so manually typing for now

export async function getSuppliersRiskRatingMap(
  supplierNumbers: string[]
  // supplierNumbers: RiskRatingOperations['getRatingsForAllSuppliers']['requestBody']['content']['application/json']
) {
  if (!Array.isArray(supplierNumbers)) {
    supplierNumbers = [supplierNumbers];
  }

  const { data, error } = await riskRatingApi.fetchClient.request(
    'post',
    '/suppliers/all',
    {
      body: supplierNumbers
    }
  );

  if (error) {
    throw new Error("Couldn't load Risk Rating info. Please try again.");
  }

  const map = new Map();

  data.forEach((supplier) =>
    map.set(supplier.supplier, supplier?.overall?.scoreString)
  );

  return map;
}

export async function getSuppliersSroSbmMap(
  supplierNumbers: NonNullable<
    NonNullable<
      SroOperations['postSroAllByPageNum']['requestBody']
    >['content']['application/json']['suppliers']
  >
) {
  if (!Array.isArray(supplierNumbers)) {
    supplierNumbers = [supplierNumbers];
  }

  let suppliersSroSbmMap = new Map();
  let pageNumber = 1;

  do {
    const { data, error } = await sroApi.fetchClient.request(
      ...sroApi.operations['postSroAllByPageNum'],
      {
        body: {
          suppliers: supplierNumbers
        },
        params: {
          path: {
            pageNum: String(pageNumber)
          }
        }
      }
    );

    if (error) {
      throw new Error("Couldn't load SRO/SBM info. Please try again.");
    }

    const responseMap = new Map(Object.entries(data.data));
    responseMap.forEach((value, key) => {
      suppliersSroSbmMap.set(key, value?.contacts?.SBM);
    });

    pageNumber = data.nextPage;
  } while (pageNumber);

  return suppliersSroSbmMap;
}

export async function getAERatingMap(
  supplierNumbers: string[] // no AE openapi spec exists
) {
  if (!Array.isArray(supplierNumbers)) {
    supplierNumbers = [supplierNumbers];
  }

  const { data, error } = await aeApi.fetchClient.request(
    'post',
    '/supplier-ratings/last-period',
    {
      body: {
        supplierNumbers: supplierNumbers
      }
    }
  );

  if (error) {
    throw new Error("Couldn't load AE Rating info. Please try again.");
  }

  const map = new Map();

  data.forEach((supplier: any) =>
    map.set(
      supplier.supplierNumber,
      supplier.classification?.trim().toLowerCase() === 'no rating'
        ? ''
        : supplier.classification || ''
    )
  );

  return map;
}

export async function getSepPlans(
  strategyNumber: SepOperations['StrategyController_getStrategyPlanDetailsByStrategyNumber']['parameters']['path']['strategyNumber']
) {
  const { data, error } = await sepApi.fetchClient.request(
    ...sepApi.operations[
      'StrategyController_getStrategyPlanDetailsByStrategyNumber'
    ],
    {
      params: {
        path: {
          strategyNumber: strategyNumber
        }
      }
    }
  );

  if (error) {
    throw new Error("Couldn't load SEP Plan info. Please try again.");
  }

  return data;
}

export async function getSepSearchResults(
  loggedInUser: NonNullable<
    SepOperations['SepProgramController_getSearchAll']['requestBody']['content']['application/json']['sbmReviewer']
  >[number]
) {
  const { data, error } = await sepApi.fetchClient.request(
    ...sepApi.operations['SepProgramController_getSearchAll'],
    {
      body: {
        sbmReviewer: [loggedInUser]
      }
    }
  );

  if (error) {
    throw new Error("Couldn't load SEP Search Results. Please try again.");
  }

  return data;
}

export async function getSupplierSpendShift(
  supplierNumbersAndUnitCodes: DmsmOperations['getSupplierSpendShift']['requestBody']['content']['application/json'],
  queryParams?: DmsmOperations['getSupplierSpendShift']['parameters']['query']
) {
  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['getSupplierSpendShift'],
    {
      body: supplierNumbersAndUnitCodes,
      params: {
        query: queryParams
      }
    }
  );

  if (error) {
    throw new Error(
      "Couldn't load Supplier Spend Shift info. Please try again."
    );
  }

  return data;
}

export async function getCiProjectSpendShift(
  supplierNumbersAndUnitCodes: DmsmOperations['getCiProjectSpendShift']['requestBody']['content']['application/json'],
  queryParams?: DmsmOperations['getCiProjectSpendShift']['parameters']['query']
): Promise<CiProjectSpendShiftApiResponse> {
  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['getCiProjectSpendShift'],
    {
      body: supplierNumbersAndUnitCodes,
      params: {
        query: queryParams
      }
    }
  );

  if (error) {
    throw new Error(
      "Couldn't load CI Project Spend Shift info. Please try again."
    );
  }

  return data as CiProjectSpendShiftApiResponse;
}

export async function searchSuppliersByQuery(query: string) {
  const { data, error } = await supplierLocationApi.fetchClient.request(
    ...supplierLocationApi.operations['getSuppliers'],
    {
      params: {
        query: {
          supplierNo: query
        }
      }
    }
  );

  if (error) {
    throw new Error("Couldn't search suppliers. Please try again.");
  }
  return data;
}

export async function getDmsmSpendPartDetails(
  requestBody: DmsmOperations['getSupplierSpendShiftPartDetail']['requestBody']['content']['application/json']
) {
  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['getSupplierSpendShiftPartDetail'],
    {
      body: requestBody
    }
  );

  if (error) {
    throw new Error("Couldn't load Spend Part Detail info. Please try again.");
  }
  return data;
}
